import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { Observable, Subject, Subscription, catchError, distinctUntilChanged, finalize, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { CommonService } from 'src/app/services/common.service';
import { initializeEditor } from 'src/app/utils/angular-editor/editorConfig';
import { helper } from 'src/environments/helper';
import { Cerere, EditRequestAssigneePayload, EditRequestPriorityPayload, EditRequestStatusPayload, UsersByCompartimentePayload } from '../../cereri.interface';
import { CereriService } from '../../cereri.service';
import { NotificationService } from 'src/app/services/notification.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { notDefaultValidator } from 'src/app/validators/not-default';
import { Generic } from 'src/app/interfaces/generic.interface';

@Component({
    selector: 'app-actualizare-cerere',
    templateUrl: './actualizare-cerere.component.html',
    styleUrls: ['./actualizare-cerere.component.scss']
})
export class ActualizareCerereComponent implements OnInit, OnChanges, OnDestroy {
    @Input() cerere!: Cerere;
    @Input() directii!: any[];
    @Input() statusCerere!: any[];
    @Input() prioritateCerere!: any[];
    @Output() cerereChanged: EventEmitter<Cerere> = new EventEmitter<Cerere>();
    @Output() istoricUpdated: EventEmitter<void> = new EventEmitter();

    private updateAsignareDestroy$ = new Subject<void>();

    filteredCompartimente: any[] = this.directii;
    compartimenteControl = new FormControl();

    filteredUsers: any[] = [];
    usersControl = new FormControl();

    passedRequestId!: number;
    type_directie_id!: number;
    type_id_compartment!: number;
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;

    editorConfig!: AngularEditorConfig;
    isLoading = false;
    selectedActionToggle = 'status';
    showSignatures: any;

    defaultChoiceParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    defaultChoiceParameterCompartiment: { name: string; id_compartiment: string } = {
        name: '-- Alegeți --',
        id_compartiment: '-1',
    };

    usersActive: any[] = [];

    sendUpdateAssigneeForm!: FormGroup;
    sendUpdateStatusForm!: FormGroup;
    sendUpdatePriorityForm!: FormGroup;

    constructor(
        private commonService: CommonService,
        private fb: FormBuilder,
        private cereriService: CereriService,
        private notificationService: NotificationService,
        public permissionsService: PermissionsService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        this.passedRequestId = this.cerere?.id_cerere;
    }

    ngOnDestroy(): void {
        this.updateAsignareDestroy$.next();
        this.updateAsignareDestroy$.complete();
    }

    ngOnInit(): void {
        this.editorConfig = initializeEditor();
        /*
        Above we call load compartimente with the type_directie_id / directii/institutii with the one inside our Cerere (check in dt to see the different directii/institutii)
        The user can still select another Directie (other than Urbanism), and also other Compartimente
        */

        this.sendUpdateAssigneeForm = this.fb.group({
            id_compartiment: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
            id_user_assigned: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
            content: new FormControl(''),
        });
        this.sendUpdateStatusForm = this.fb.group({
            current_status: new FormControl('-1', [Validators.required, notDefaultValidator('-1')]),
            message: new FormControl(''),
        });
        this.sendUpdatePriorityForm = this.fb.group({
            id_cerere_priority: new FormControl('-1', [Validators.required, notDefaultValidator('-1')]),
            message: new FormControl(''),
        });

        this.filteredCompartimente = this.directii;

        this.renderFieldsSearch();
    }

    renderFieldsSearch() {
        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.updateAsignareDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredCompartimente = this.directii;
                    return of(this.directii);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredCompartimente = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.updateAsignareDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterUsers(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredUsers = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    filterCompartimente(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.directii.filter(compartiment =>
            this.normalizeString(compartiment.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    onCompartimentSelectClosed() {
        const selectedValue = this.compartimenteControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredCompartimente.some(categorie =>
                categorie.id_compartiment === this.sendUpdateAssigneeForm.get('id_compartiment')?.value
            );

            if (!isSelectedInFilteredList) {
                this.compartimenteControl.setValue(null);
                this.filteredCompartimente = this.directii;
                this.updateAsignareDestroy$.next();
                this.updateAsignareDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    onCompartimentChange(compartimentId: number) {
        this.sendUpdateAssigneeForm.controls['id_user_assigned'].patchValue(-1);

        if (compartimentId == -1) {
            this.usersActive = [];
            this.filteredUsers = [];
            return;
        }

        if (compartimentId) {
            this.loadUsers(compartimentId);
        }
    }

    filterUsers(searchTerm: string): Observable<Generic[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        return of(this.usersActive.filter(user =>
            this.normalizeString(user.name).includes(normalizedSearchTerm)
        ));
    }

    onUsersSelectClosed() {
        const selectedValue = this.usersControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredUsers.some(user =>
                user.id === this.sendUpdateAssigneeForm.get('id_user_assigned')?.value
            );

            if (!isSelectedInFilteredList) {
                this.usersControl.setValue(null);
                this.filteredUsers = this.usersActive;
                this.updateAsignareDestroy$.next();
                this.updateAsignareDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadUsers(compartimentId: number) {
        this.isLoading = true;
        let payload: UsersByCompartimentePayload = { compartiment_id: compartimentId };

        let usersObservable$ = compartimentId
            ? this.cereriService.getActiveUsersByCompartiment(payload)
            : this.commonService.getUsersActive();

        usersObservable$.pipe(
            map((res: any) => [...res]),
            finalize(() => (this.isLoading = false)),
        ).subscribe({
            next: (users) => {
                this.usersActive = [...users];
                this.filteredUsers = [...users];
            },
            error: (err) => {
                console.log(err);
                this.filteredUsers = [];
            }
        });
    }

    onActionChange(event: MatButtonToggleChange) {
        this.selectedActionToggle = event.value;
    }

    submitUpdateAssigneeMessage() {
        this.isLoading = true;

        const newRequestAssignee: EditRequestAssigneePayload = {
            ...this.sendUpdateAssigneeForm.value,
        };

        this.cereriService
            .updateRequestAssignee(this.cerere!.id_cerere, newRequestAssignee)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.isLoading = true;
                    this.notificationService.warningSwal(
                        this.successTitle,
                        'Cerere actualizată cu succes!',
                        this.successIcon
                    );
                    this.getRequestInfo();
                    this.sendUpdateAssigneeForm.patchValue({
                        id_compartiment: -1,
                        id_user_assigned: -1,
                        content: '',
                    });

                    this.sendUpdateAssigneeForm.controls['id_compartiment'].setErrors(null);
                    this.sendUpdateAssigneeForm.controls['id_user_assigned'].setErrors(null);
                    this.sendUpdateAssigneeForm.markAsUntouched();
                    this.sendUpdateAssigneeForm.updateValueAndValidity();
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    submitUpdateStatusMessage() {
        this.isLoading = true;

        const formValues = this.sendUpdateStatusForm.value;

        const newRequestStatus: EditRequestStatusPayload = {
            ...formValues,
            id_cerere: this.cerere?.id_cerere,
        };

        this.cereriService
            .updateRequestStatus(this.cerere!.id_cerere, newRequestStatus)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.isLoading = true;
                    this.notificationService.warningSwal(
                        this.successTitle,
                        'Cerere actualizată cu succes!',
                        this.successIcon
                    );
                    this.getRequestInfo();
                    this.sendUpdateStatusForm.patchValue({
                        current_status: this.sendUpdateStatusForm.get('current_status')?.value,
                        message: '',
                    });
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    submitUpdatePriorityMessage() {
        this.isLoading = true;

        const formValues = this.sendUpdatePriorityForm.value;

        const newRequestPriority: EditRequestPriorityPayload = {
            ...formValues,
            id_cerere: this.cerere?.id_cerere,
        };

        this.cereriService
            .updateRequestPriority(this.cerere!.id_cerere, newRequestPriority)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.isLoading = true;
                    this.notificationService.warningSwal(
                        this.successTitle,
                        'Cerere actualizată cu succes!',
                        this.successIcon
                    );
                    this.getRequestInfo();
                    this.sendUpdatePriorityForm.patchValue({
                        id_cerere_priority: this.sendUpdatePriorityForm.get('id_cerere_priority')?.value,
                        message: '',
                    });
                },
                error: (err) => {
                    let msg;
                    try {
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    getRequestInfo() {
        this.isLoading = true;
        this.cereriService
            .getRequestById(this.passedRequestId)
            .pipe(map((response: any) => response.data))
            .subscribe((requestData) => {
                this.isLoading = false;
                this.cerere = requestData;
                this.cerereChanged.emit(this.cerere);
                this.istoricUpdated.emit();
                if (
                    typeof this.cerere?.generabile !== 'undefined' &&
                    this.cerere.generabile.length > 0
                ) {
                    this.showSignatures = this.cerere.generabile[0].id_document;
                }
            });
    }
}
